import {
  Alert,
  AlertDescription,
  Box,
  Button,
  ButtonGroup,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  ListItem,
  Spinner,
  Text,
  Tooltip,
  UnorderedList,
  useToast,
} from "@chakra-ui/react";
import { LocalDate, LocalDateTime } from "@js-joda/core";
import React from "react";
import { FiEdit3, FiFileText, FiRefreshCw, FiSlash } from "react-icons/fi";
import { ResponseOf } from "../../core/api";
import SingleDatePicker from "../../shared/components/DatePicker/SingleDatePicker";
import useStableCallback from "../../shared/hooks/useStableCallback";
import { PatientDocumentTypeId, PatientTaskInstanceId } from "../../shared/schema/schema";
import { dateFormatter } from "../../shared/utils/date-formatter";
import CommentPopover, { stopEscapePropagation } from "./CommentPopover";
import ConfirmDialog from "./ConfirmDialog";
import DocumentPreview from "./DocumentPreview";
import DocumentQuestionList from "./DocumentQuestionList";
import {
  type EntityEditorActivity,
  IDLE_ENTITY_ACTIVITY,
  ReviewEntityProvider,
  type ReviewEntityContextValue,
} from "./entity-editors/reviewEntityContext";
import {
  LayoutBlock,
  findShownQuestionId,
  flattenContent,
  indexAnswers,
  isEntityBacked,
  layoutRows,
  listQuestions,
  mobileHiddenQuestionIds,
  replacePlaceholders,
} from "./layout/documentLayout";
import { firstAnswer } from "./question-types/answerFormat";
import { NO_CHART_ROW, type AnswerWrite } from "./question-types/answerWrites";
import { getQuestionType, type DocumentLayoutType } from "./question-types/registry";
import QuestionBlock from "./QuestionBlock";
import { DraftCommentList, ReviewActionsFooter, type SubmitDateChoice } from "./ReviewActions";
import ReviewHeader from "./ReviewHeader";
import {
  approvalAction,
  canRegeneratePdf,
  showsResubmit,
  closeBlock,
  discardSummary,
  inlineEditUnavailableReason,
  questionActions,
  reopenBlockedReason,
  reviewCapabilities,
  type CloseBlock,
} from "./reviewRules";
import ReviewSidePanel, { DocumentQuestionComment } from "./ReviewSidePanel";
import ScannedDocumentView from "./ScannedDocumentView";
import { missingRequiredAnswers, type MissingRequiredAnswer } from "./submitRules";
import { errorText, useReviewActions } from "./useReviewActions";
import { useReviewCloseGuard } from "./useReviewCloseGuard";
import { needsResubmit, useReviewSession } from "./useReviewSession";

export type DocumentReview = ResponseOf<
  "get",
  "./patient_task_instances/:patientTaskInstanceId/patient_documents/:documentTypeId/review"
>;

const SIDE_PANEL_WIDTH = "380px";
const HIGHLIGHT_DURATION_MS = 1600;

type ReviewLayout =
  | { type: "scanned" }
  | { type: "preview"; blocks: LayoutBlock[]; rows: ReturnType<typeof layoutRows> }
  | { type: "list"; blocks: LayoutBlock[] };

function buildLayout(review: DocumentReview): ReviewLayout {
  const { document } = review;
  if (review.isScanned) {
    return { type: "scanned" };
  }
  const answers = indexAnswers(document.answers);
  if (document.documentTypeType === "GENERAL" && document.htmlVersionId === null) {
    const rows = layoutRows(document.content, answers);
    return { type: "preview", rows, blocks: rows.flatMap((row) => row.blocks) };
  }
  const blocks = listQuestions(document.content, answers).map(
    (question): LayoutBlock => ({
      question,
      width: null,
      answers: answers.get(question.id) ?? [],
    })
  );
  return { type: "list", blocks };
}

type PendingConfirm =
  | { type: "reopen" }
  /**
   * Required questions without an answer, found when Submit was clicked, and the submitted date
   * picked from its menu.
   */
  | { type: "submit"; date: SubmitDateChoice; missing: MissingRequiredAnswer[] }
  /** Closing with edits not submitted: `leave` closes (or re-runs the stopped navigation). */
  | { type: "submitOnClose"; leave: () => void }
  | { type: "withdraw"; comment: DocumentQuestionComment }
  /** `proceed` closes the review (or re-runs the navigation that was stopped). */
  | { type: "discard"; proceed: () => void };

interface Props {
  closeRequestRef: React.MutableRefObject<(() => void) | null>;
  documentTypeId: PatientDocumentTypeId;
  isRefreshing: boolean;
  /** The last background refetch failed: the review shows its previous data. */
  refreshError: Error | null;
  review: DocumentReview;
  taskInstanceId: PatientTaskInstanceId;
  onClose: () => void;
  onRefresh: () => void;
}

export default function DocumentReviewContent(props: Props) {
  const { review, taskInstanceId, documentTypeId } = props;
  const { document, header } = review;
  const toast = useToast({ position: "top" });
  const session = useReviewSession();
  const actions = useReviewActions({ documentTypeId, review, session, taskInstanceId });

  const previewRef = React.useRef<HTMLDivElement>(null);
  const [highlightedQuestionId, setHighlightedQuestionId] = React.useState<number | null>(null);
  const highlightTimeoutRef = React.useRef<number | undefined>(undefined);
  const [editingQuestionId, setEditingQuestionId] = React.useState<number | null>(null);
  const [commentingQuestionId, setCommentingQuestionId] = React.useState<number | null>(null);
  const [pendingConfirm, setPendingConfirm] = React.useState<PendingConfirm | null>(null);
  // The day picked in the Submit dialog for "Submit with custom date"
  const [customSubmitDate, setCustomSubmitDate] = React.useState<LocalDate | null>(null);
  // What the open patient-record editor reports (its open forms, a change in flight)
  const [entityActivity, setEntityActivity] =
    React.useState<EntityEditorActivity>(IDLE_ENTITY_ACTIVITY);
  // The generated PDF, shown in place of the preview. Only for submitted documents that have one;
  // scanned documents already show their file.
  const [isShowingPdf, setIsShowingPdf] = React.useState(false);

  React.useEffect(() => () => window.clearTimeout(highlightTimeoutRef.current), []);

  const answers = React.useMemo(() => indexAnswers(document.answers), [document.answers]);
  const byId = React.useMemo(
    () => new Map(flattenContent(document.content).map((item) => [item.id, item])),
    [document.content]
  );
  const mobileHiddenIds = React.useMemo(
    () => mobileHiddenQuestionIds(document.content),
    [document.content]
  );
  const layout = React.useMemo(() => buildLayout(review), [review]);
  const shownIds = React.useMemo(
    () => new Set(layout.type === "scanned" ? [] : layout.blocks.map((block) => block.question.id)),
    [layout]
  );
  const documentState = {
    // The header names the task's caregiver; there is none when nobody is assigned
    hasCaregiver: header.caregiverName !== null,
    hasScheduledDoc: review.scheduledDocId !== null,
    isSubmitted: review.isSubmitted,
    layoutType: layout.type,
  };
  const capabilities = reviewCapabilities(documentState);
  const editUnavailableReason = inlineEditUnavailableReason(documentState);

  const questionLabels = React.useMemo(() => {
    const labels = new Map<number, string>();
    for (const question of byId.values()) {
      labels.set(question.id, replacePlaceholders(question.label, header));
    }
    return labels;
  }, [byId, header]);

  /** Open comments per shown block (a chart row's comments count on its chart). */
  const openCommentCounts = React.useMemo(() => {
    const counts = new Map<number, number>();
    for (const comment of review.comments) {
      if (comment.state !== "open") {
        continue;
      }
      const shownId = findShownQuestionId(comment.questionId, byId, shownIds);
      if (shownId !== null) {
        counts.set(shownId, (counts.get(shownId) ?? 0) + 1);
      }
    }
    return counts;
  }, [review.comments, byId, shownIds]);

  const draftTexts = React.useMemo(
    () => new Map(session.drafts.map((draft) => [draft.questionId, draft.text])),
    [session.drafts]
  );

  // ---- Closing: never while a save or submit is in flight; ask before dropping unsent work. With
  // edits of a submitted document not submitted again, closing (and in-app navigation / Back)
  // asks whether to submit them first. ----

  // Text typed in the open comment popover that is not a draft yet (it becomes one when the
  // popover finishes closing). The ref has the text; the state only flips (for the unload prompt).
  const unsavedCommentRef = React.useRef<{ questionId: number; text: string } | null>(null);
  const [hasUnsavedComment, setHasUnsavedComment] = React.useState(false);
  const setUnsavedCommentText = (questionId: number, text: string | null) => {
    if (text !== null) {
      unsavedCommentRef.current = { questionId, text };
    } else if (unsavedCommentRef.current?.questionId === questionId) {
      // Only the popover that reported it clears it (another one may have opened meanwhile)
      unsavedCommentRef.current = null;
    }
    setHasUnsavedComment(unsavedCommentRef.current !== null);
  };

  const isEditing = editingQuestionId !== null;
  // A patient-record editor saves each change as it is made: only its open forms are unsaved
  const editingQuestion = editingQuestionId !== null ? byId.get(editingQuestionId) : undefined;
  const isEditingAnswer = editingQuestion !== undefined && !isEntityBacked(editingQuestion);
  const hasUnsubmittedEdits = needsResubmit({
    editedCount: actions.unsubmittedEdits,
    hasScheduledDoc: review.scheduledDocId !== null,
    isSubmitted: review.isSubmitted,
  });
  const getCloseBlock = () =>
    closeBlock({
      hasOpenRecordForm: entityActivity.hasOpenForm,
      hasUnsavedCommentText: unsavedCommentRef.current !== null || hasUnsavedComment,
      hasUnsentWork: session.hasUnsentWork,
      isEditing: isEditingAnswer,
      isSaving: actions.isSaving,
      isSavingRecord: entityActivity.isSaving,
      isSubmitting: actions.isSubmitting,
    });
  const isSavingAnything = actions.isSaving || entityActivity.isSaving;

  const handleCloseBlocked = (block: CloseBlock, proceed: () => void) => {
    switch (block) {
      case "saving":
        toast({
          title: "Saving your change…",
          description: "The review can be closed once it is saved.",
          status: "info",
          duration: 3000,
        });
        return;
      case "submitting":
        toast({
          title: "Submitting the document…",
          description: "The review can be closed once it is submitted.",
          status: "info",
          duration: 3000,
        });
        return;
      case "unsentWork": {
        // Keep the open popover's text as its draft now, so the dialog counts it and "Keep
        // reviewing" finds it in the drafts.
        const unsaved = unsavedCommentRef.current;
        if (unsaved !== null) {
          session.setDraft(unsaved.questionId, unsaved.text);
        }
        setCommentingQuestionId(null);
        setPendingConfirm({ type: "discard", proceed });
        return;
      }
    }
  };

  /**
   * Leaves the review, first asking whether to submit the edits of a submitted document that were
   * not submitted again.
   */
  const leaveAfterSubmitCheck = (leave: () => void) => {
    if (!hasUnsubmittedEdits) {
      leave();
      return;
    }
    // The reviewer agreed to drop the open editor: nothing may save while the dialog is open
    if (editingQuestionId !== null) {
      closeEditor();
    }
    setPendingConfirm({ type: "submitOnClose", leave });
  };

  const closeGuard = useReviewCloseGuard({
    getCloseBlock,
    mustAskToSubmit: () => hasUnsubmittedEdits,
    warnOnUnload: getCloseBlock() !== null || hasUnsubmittedEdits,
    // In-app navigation and browser Back / Forward ask like the close button
    onAskToSubmit: leaveAfterSubmitCheck,
    onBlocked: (block, proceed) => handleCloseBlocked(block, () => leaveAfterSubmitCheck(proceed)),
  });

  /** The review goes away for good (the guard lets this navigation through). */
  const closeNow = () => {
    closeGuard.allowLeave();
    props.onClose();
  };

  /**
   * Close button / Escape: ask before dropping unsent work, then, with edits not submitted, ask
   * whether to submit them.
   */
  const requestClose = () => {
    const proceed = () => leaveAfterSubmitCheck(closeNow);
    const block = getCloseBlock();
    if (block === null) {
      proceed();
      return;
    }
    handleCloseBlocked(block, proceed);
  };

  const { closeRequestRef } = props;
  React.useLayoutEffect(() => {
    closeRequestRef.current = requestClose;
  });
  React.useEffect(
    () => () => {
      closeRequestRef.current = null;
    },
    [closeRequestRef]
  );

  // ---- Question interactions ----

  const scrollToQuestion = (questionId: number) => {
    const element = previewRef.current?.querySelector(`[data-question-id="${questionId}"]`);
    element?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const handleSelectComment = (comment: DocumentQuestionComment) => {
    const shownId = findShownQuestionId(comment.questionId, byId, shownIds);
    if (shownId === null) {
      return;
    }
    if (isShowingPdf) {
      // The preview stays mounted while hidden; scroll once it is visible again.
      setIsShowingPdf(false);
      window.requestAnimationFrame(() => scrollToQuestion(shownId));
    } else {
      scrollToQuestion(shownId);
    }
    setHighlightedQuestionId(shownId);
    window.clearTimeout(highlightTimeoutRef.current);
    highlightTimeoutRef.current = window.setTimeout(
      () => setHighlightedQuestionId(null),
      HIGHLIGHT_DURATION_MS
    );
  };

  const startEdit = (questionId: number) => {
    actions.resetSave();
    setCommentingQuestionId(null);
    setEditingQuestionId(questionId);
  };

  /** After a successful save (the save state is idle again by then). */
  const closeEditor = () => {
    actions.resetSave();
    setEditingQuestionId(null);
    setEntityActivity(IDLE_ENTITY_ACTIVITY);
  };

  const cancelEdit = () => {
    if (actions.isSaving) {
      return;
    }
    closeEditor();
  };

  const openDraft = (questionId: number) => {
    scrollToQuestion(questionId);
    setCommentingQuestionId(questionId);
  };

  const blockLayoutType: DocumentLayoutType = layout.type === "list" ? "list" : "preview";

  // Entity-backed questions (medications, codes, physician) show and edit the patient's records
  const recordEntityChange = useStableCallback(actions.recordEntityChange);
  const entityContext = React.useMemo(
    (): ReviewEntityContextValue => ({
      content: document.content,
      documentType: document.documentTypeType,
      patientContractId: header.patientContractId,
      patientId: header.patientId,
      scheduledDocId: review.scheduledDocId,
      onActivityChange: setEntityActivity,
      onEntityChanged: recordEntityChange,
    }),
    [
      document.content,
      document.documentTypeType,
      header.patientContractId,
      header.patientId,
      review.scheduledDocId,
      recordEntityChange,
    ]
  );

  const renderBlock = (block: LayoutBlock) => {
    const { question } = block;
    const allowed = questionActions(capabilities, question, mobileHiddenIds);
    const { Editor } = getQuestionType(question.itemType);
    const isEditingThis = allowed.edit && editingQuestionId === question.id && Editor !== null;
    // Outside a chart the answer keeps the row it is stored at (-1 when there is none yet)
    const chartRow = block.answers[0]?.chartRow ?? NO_CHART_ROW;
    const saveAnswers = (writes: AnswerWrite[]) =>
      actions.saveAnswers(writes, { editedQuestionId: question.id });
    const draftText = draftTexts.get(question.id) ?? null;
    const openCount = openCommentCounts.get(question.id) ?? 0;

    const blockElement = (
      <QuestionBlock
        answers={answers}
        block={block}
        byId={byId}
        commentCount={openCount + (draftText !== null ? 1 : 0)}
        editor={
          isEditingThis ? (
            // Escape in the editor (or its date picker) must not close the whole review.
            <Box onKeyDown={stopEscapePropagation}>
              <Editor
                answers={answers}
                byId={byId}
                chartRow={chartRow}
                error={
                  actions.saveError !== null
                    ? errorText(
                        actions.saveError,
                        "Could not save the answer. Check your connection and try again."
                      )
                    : null
                }
                isSaving={actions.isSaving}
                layoutType={blockLayoutType}
                placeholders={header}
                question={question}
                value={firstAnswer(block.answers) ?? null}
                onCancel={cancelEdit}
                onClose={closeEditor}
                onSave={(value) => {
                  // On failure the editor stays open with the error (its Retry saves again)
                  saveAnswers([{ questionId: question.id, chartRow, answer: value }]).then(
                    closeEditor,
                    () => undefined
                  );
                }}
                onSaveAnswers={saveAnswers}
              />
            </Box>
          ) : null
        }
        hasDraft={draftText !== null}
        isActive={commentingQuestionId === question.id}
        isEdited={session.editedQuestionIds.has(question.id)}
        isHighlighted={highlightedQuestionId === question.id}
        layoutType={blockLayoutType}
        placeholders={header}
        onComment={allowed.comment ? () => setCommentingQuestionId(question.id) : undefined}
        // One inline editor at a time
        onEdit={
          allowed.edit && !isEditing && !actions.isSubmitting
            ? () => startEdit(question.id)
            : undefined
        }
      />
    );

    if (!allowed.comment) {
      return blockElement;
    }
    return (
      <CommentPopover
        draftText={draftText}
        isOpen={commentingQuestionId === question.id}
        questionLabel={questionLabels.get(question.id) ?? question.label}
        onAutoSave={(text) => session.setDraft(question.id, text)}
        onClose={() => setCommentingQuestionId(null)}
        onDirtyTextChange={(text) => setUnsavedCommentText(question.id, text)}
        onRemove={() => {
          session.removeDraft(question.id);
          setCommentingQuestionId(null);
        }}
        onSave={(text) => {
          session.setDraft(question.id, text);
          setCommentingQuestionId(null);
        }}
      >
        {blockElement}
      </CommentPopover>
    );
  };

  const renderDocument = () => {
    switch (layout.type) {
      case "scanned":
        return <ScannedDocumentView pdfUrl={document.pdfUrl} title={document.title} />;
      case "preview":
        return (
          <DocumentPreview
            header={header}
            renderBlock={renderBlock}
            rows={layout.rows}
            submittedAt={document.submittedAt}
            title={document.title}
          />
        );
      case "list":
        return (
          <DocumentQuestionList
            blocks={layout.blocks}
            header={header}
            renderBlock={renderBlock}
            submittedAt={document.submittedAt}
            title={document.title}
          />
        );
    }
  };

  // ---- Side panel ----

  const reopenBlocked = reopenBlockedReason({
    hasOpenRecordForm: entityActivity.hasOpenForm,
    isEditing: isEditingAnswer,
    isSaving: actions.isSaving,
    isSavingRecord: entityActivity.isSaving,
  });

  const handleConfirmReopen = () => {
    if (reopenBlocked !== null) {
      setPendingConfirm(null);
      return;
    }
    actions.reopen({
      onSuccess: () => setCommentingQuestionId(null),
      onSettled: () => setPendingConfirm(null),
    });
  };

  const handleSubmitClick = (date: SubmitDateChoice) => {
    if (reopenBlocked === null) {
      setCustomSubmitDate(null);
      setPendingConfirm({
        type: "submit",
        date,
        missing: missingRequiredAnswers(document.content, answers),
      });
    }
  };

  const confirmSubmit = (date: SubmitDateChoice) => {
    if (date === "custom") {
      if (customSubmitDate === null) {
        return;
      }
      void actions.submit({ type: "custom", date: customSubmitDate });
    } else {
      void actions.submit({ type: "original" });
    }
    setPendingConfirm(null);
  };

  const describeMissing = (item: MissingRequiredAnswer) => {
    const label = questionLabels.get(item.question.id) ?? `Question ${item.question.id}`;
    return item.chartRow >= 0 ? `${label} (row ${item.chartRow + 1})` : label;
  };

  // A submitted document whose PDF does not show every change: warn and offer Submit
  const resubmitNotice = !showsResubmit(review, actions.unsubmittedEdits)
    ? null
    : review.hasAnswersNewerThanPdf && review.pdfGeneratedAt !== null
    ? `Some answers were changed after the PDF was generated (${dateFormatter.toDateTime(
        review.pdfGeneratedAt
      )}). Submit the document again to apply them.`
    : "Some changes are not applied yet. Submit the document again to apply them.";

  // The server closes the agency's open comments on the document when it is submitted
  const openAgencyCommentCount = review.comments.filter(
    (comment) => comment.source === "agency" && comment.state === "open"
  ).length;

  const renderSubmitBody = (pending: Extract<PendingConfirm, { type: "submit" }>) => {
    const dateText = (() => {
      switch (pending.date) {
        case "original":
          return review.isSubmitted
            ? "It keeps its original submitted date."
            : "It is submitted with today's date.";
        case "custom":
          return "It is submitted with the date below.";
      }
    })();
    return (
      <Flex direction="column" gap={3}>
        {pending.missing.length > 0 ? (
          <Box>
            {pending.missing.length === 1
              ? "This required question has no answer:"
              : `These ${pending.missing.length} required questions have no answer:`}
            <UnorderedList mt={2} spacing={0.5}>
              {pending.missing.map((item) => (
                <ListItem key={`${item.question.id}:${item.chartRow}`}>
                  {describeMissing(item)}
                </ListItem>
              ))}
            </UnorderedList>
          </Box>
        ) : (
          <Text>The document will count as submitted with the answers shown here.</Text>
        )}
        <Text>
          {dateText}
          {openAgencyCommentCount > 0 && " Its open comments are closed."}
        </Text>
        {pending.date === "custom" && (
          // Escape in the date picker must not close the dialog
          <FormControl isRequired onKeyDown={stopEscapePropagation}>
            <FormLabel fontSize="sm">Submitted date</FormLabel>
            <SingleDatePicker
              maxDate={LocalDateTime.now()}
              selected={customSubmitDate}
              onChange={(date) => setCustomSubmitDate(date)}
            />
          </FormControl>
        )}
      </Flex>
    );
  };

  const renderCommentActions = (comment: DocumentQuestionComment) => {
    // Only the agency's own sent comments, while the RN still sees them as open.
    if (comment.source !== "agency" || comment.state !== "open") {
      return null;
    }
    return (
      <Button
        color="gray.600"
        isLoading={actions.withdrawingRejectionId === comment.id}
        leftIcon={<Icon as={FiSlash} boxSize={3} />}
        size="xs"
        variant="ghost"
        onClick={() => setPendingConfirm({ type: "withdraw", comment })}
      >
        Withdraw
      </Button>
    );
  };

  // Every completed document offers its generated PDF; scanned ones already show their file. The
  // server sends no link when the file can't be found in storage, so say so instead of hiding it.
  const pdfUrl = document.pdfUrl;
  const offersPdf = layout.type !== "scanned" && review.isSubmitted;
  const showPdf = offersPdf && pdfUrl !== null && isShowingPdf;
  // Generated in the background (e.g. after a submit); the review polls until it is ready
  const isPdfGenerating = offersPdf && review.isPdfGenerating;

  const draftCount = session.drafts.length;
  const hasGeneralComment = session.generalComment.trim() !== "";

  const notice = (
    <>
      {editUnavailableReason !== null && (
        <Alert borderRadius="md" fontSize="xs" mt={3} px={3} py={2} status="info">
          <AlertDescription>{editUnavailableReason}</AlertDescription>
        </Alert>
      )}
      {props.refreshError !== null && (
        <Alert borderRadius="md" fontSize="xs" mt={3} px={3} py={2} status="warning">
          <AlertDescription flex={1}>
            Could not refresh this document; it may be out of date. Your comments are kept.
          </AlertDescription>
          <Button isLoading={props.isRefreshing} ml={2} size="xs" onClick={props.onRefresh}>
            Retry
          </Button>
        </Alert>
      )}
    </>
  );

  return (
    <ReviewEntityProvider value={entityContext}>
      <ReviewHeader
        closeDisabledReason={
          isSavingAnything
            ? "Saving your change…"
            : actions.isSubmitting
            ? "Submitting the document…"
            : null
        }
      >
        <Box minW={0}>
          <Heading fontWeight="semibold" noOfLines={1} size="md">
            {document.title}
          </Heading>
          <Text color="gray.500" fontSize="sm" noOfLines={1}>
            {header.patientName}
            {header.caregiverName !== null && ` · ${header.caregiverName}`}
          </Text>
        </Box>
        {offersPdf && (
          <ButtonGroup isAttached flexShrink={0} ml="auto" size="sm" variant="outline">
            <Button
              aria-pressed={!showPdf}
              colorScheme={showPdf ? "gray" : "blue"}
              leftIcon={<Icon as={FiEdit3} boxSize={3.5} />}
              variant={showPdf ? "outline" : "solid"}
              onClick={() => setIsShowingPdf(false)}
            >
              Review
            </Button>
            <Tooltip
              hasArrow
              shouldWrapChildren
              isDisabled={pdfUrl !== null}
              label={
                isPdfGenerating
                  ? "The PDF is being generated. It opens here once it is ready."
                  : "The generated PDF isn't available. Use Regenerate PDF in the side panel to create it."
              }
              placement="bottom-end"
            >
              <Button
                aria-pressed={showPdf}
                borderLeftRadius={0}
                colorScheme={showPdf ? "blue" : "gray"}
                isDisabled={pdfUrl === null}
                isLoading={pdfUrl === null && isPdfGenerating}
                leftIcon={<Icon as={FiFileText} boxSize={3.5} />}
                loadingText="PDF"
                variant={showPdf ? "solid" : "outline"}
                onClick={() => setIsShowingPdf(true)}
              >
                PDF
              </Button>
            </Tooltip>
          </ButtonGroup>
        )}
      </ReviewHeader>
      <Flex flex={1} minH={0}>
        {showPdf && (
          <Box flex={1} minW={0} overflow="hidden">
            <ScannedDocumentView pdfUrl={pdfUrl} title={document.title} />
          </Box>
        )}
        {/* Kept mounted while the PDF shows, so open editors, comments and scroll survive. */}
        <Box
          ref={previewRef}
          display={showPdf ? "none" : "block"}
          flex={1}
          minW={0}
          overflowY={layout.type === "scanned" ? "hidden" : "auto"}
          px={layout.type === "scanned" ? 0 : 6}
          py={layout.type === "scanned" ? 0 : 8}
        >
          {renderDocument()}
        </Box>
        <Box borderColor="gray.200" borderLeftWidth={1} flexShrink={0} w={SIDE_PANEL_WIDTH}>
          <ReviewSidePanel
            approvedAt={review.approvedAt}
            beforeComments={
              draftCount > 0 ? (
                <DraftCommentList
                  drafts={session.drafts}
                  questionLabels={questionLabels}
                  onEdit={openDraft}
                  onRemove={session.removeDraft}
                />
              ) : null
            }
            canLocateQuestion={(questionId) =>
              findShownQuestionId(questionId, byId, shownIds) !== null
            }
            comments={review.comments}
            footer={
              <ReviewActionsFooter
                approval={approvalAction(review)}
                canReopen={capabilities.canReopen}
                draftCount={draftCount}
                generalComment={session.generalComment}
                hasUnsentWork={session.hasUnsentWork}
                isApproving={actions.isApproving}
                isReopening={actions.isReopening}
                isSubmitting={actions.isSubmitting}
                reopenBlockedReason={reopenBlocked}
                resubmitNotice={resubmitNotice}
                submitBlockedReason={reopenBlocked}
                onApprove={() => void actions.approve()}
                onGeneralCommentChange={session.setGeneralComment}
                onMarkSubmitted={() => void actions.markSubmitted()}
                onReopen={() => setPendingConfirm({ type: "reopen" })}
                onSubmit={handleSubmitClick}
              />
            }
            isOpenForResubmission={review.isOpenForResubmission}
            isRefreshing={props.isRefreshing}
            isSubmitted={review.isSubmitted}
            notice={notice}
            pdfActions={
              isPdfGenerating ? (
                <Flex align="center" color="gray.600" fontSize="xs" gap={1.5}>
                  <Spinner size="xs" />
                  Generating PDF…
                </Flex>
              ) : canRegeneratePdf(review) ? (
                // Disabled while a change is saving: the PDF would miss it
                <Button
                  color="gray.600"
                  isDisabled={isSavingAnything || actions.isSubmitting}
                  isLoading={actions.isRegeneratingPdf}
                  leftIcon={<Icon as={FiRefreshCw} boxSize={3} />}
                  loadingText="Regenerating…"
                  size="xs"
                  variant="ghost"
                  onClick={actions.regeneratePdfNow}
                >
                  Regenerate PDF
                </Button>
              ) : null
            }
            pdfUrl={document.pdfUrl}
            questionLabels={questionLabels}
            renderCommentActions={renderCommentActions}
            submittedAt={document.submittedAt}
            onRefresh={props.onRefresh}
            onSelectComment={handleSelectComment}
          />
        </Box>
      </Flex>

      <ConfirmDialog
        body={
          <>
            The RN will be notified and the document goes back to them
            {draftCount > 0 &&
              ` with ${draftCount} question ${draftCount === 1 ? "comment" : "comments"}`}
            {hasGeneralComment &&
              (draftCount > 0 ? " and your general comment" : " with your general comment")}
            . It will no longer count as submitted until they resubmit it.
          </>
        }
        colorScheme="orange"
        confirmLabel="Reopen for RN"
        isLoading={actions.isReopening}
        isOpen={pendingConfirm?.type === "reopen"}
        title="Reopen this document?"
        onCancel={() => setPendingConfirm(null)}
        onConfirm={handleConfirmReopen}
      />
      <ConfirmDialog
        body={pendingConfirm?.type === "submit" ? renderSubmitBody(pendingConfirm) : null}
        colorScheme="green"
        confirmLabel={
          pendingConfirm?.type === "submit" && pendingConfirm.missing.length > 0
            ? "Submit anyway"
            : "Submit"
        }
        isConfirmDisabled={
          pendingConfirm?.type === "submit" &&
          pendingConfirm.date === "custom" &&
          customSubmitDate === null
        }
        isOpen={pendingConfirm?.type === "submit"}
        title={review.isSubmitted ? "Submit this document again?" : "Submit this document?"}
        onCancel={() => setPendingConfirm(null)}
        onConfirm={() => {
          if (pendingConfirm?.type === "submit") {
            confirmSubmit(pendingConfirm.date);
          }
        }}
      />
      <ConfirmDialog
        body={
          <>
            Your changes are saved, but they are not applied to the submitted document until it is
            submitted again.
            {openAgencyCommentCount > 0 && " Submitting closes its open comments."}
          </>
        }
        cancelLabel="Keep reviewing"
        colorScheme="green"
        confirmLabel="Submit with original date"
        isLoading={actions.isSubmitting}
        isOpen={pendingConfirm?.type === "submitOnClose"}
        secondaryLabel="Close without submitting"
        title="Submit your changes?"
        onCancel={() => setPendingConfirm(null)}
        onConfirm={() => {
          if (pendingConfirm?.type === "submitOnClose") {
            // On failure the dialog stays open (with the error toast) to retry or close anyway
            void actions.submit({ type: "original" }, { onSuccess: pendingConfirm.leave });
          }
        }}
        onSecondary={() => {
          if (pendingConfirm?.type === "submitOnClose") {
            setPendingConfirm(null);
            pendingConfirm.leave();
          }
        }}
      />
      <ConfirmDialog
        body="The RN will no longer be asked to fix this. The comment stays in the history as withdrawn."
        colorScheme="red"
        confirmLabel="Withdraw"
        isLoading={actions.isWithdrawing}
        isOpen={pendingConfirm?.type === "withdraw"}
        title="Withdraw this comment?"
        onCancel={() => setPendingConfirm(null)}
        onConfirm={() => {
          if (pendingConfirm?.type === "withdraw") {
            actions.withdraw(pendingConfirm.comment, () => setPendingConfirm(null));
          }
        }}
      />
      <ConfirmDialog
        body={discardSummary({
          draftCount,
          hasGeneralComment,
          hasOpenRecordForm: entityActivity.hasOpenForm,
          isEditing: isEditingAnswer,
        })}
        cancelLabel="Keep reviewing"
        colorScheme="red"
        confirmLabel="Discard and close"
        isOpen={pendingConfirm?.type === "discard"}
        title="Discard unsent work?"
        onCancel={() => setPendingConfirm(null)}
        onConfirm={() => {
          if (pendingConfirm?.type === "discard") {
            setPendingConfirm(null);
            pendingConfirm.proceed();
          }
        }}
      />
    </ReviewEntityProvider>
  );
}
